import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../environments/environment';
import * as msalConfig from './config/msal.config';

/**
 * Makes sure that the user is authenticated and authorized before the application continues
 */
@Injectable()
export class AppAuthGuard {
  /**
   * Constructor
   * @param router Router
   * @param http HttpClient
   * @param injector Injector
   */
  constructor(public router: Router, private http: HttpClient, private injector: Injector) { }

  /**
   * Checks if the application can activate the given route
   * @param _next ActivatedRouteSnapshot
   * @param _state RouterStateSnapshot
   */
  public canActivate(_next: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean | Observable<boolean> {
    console.log('Validating user data and licensing...');
    return new Observable<boolean>((observer) => {

      if (!environment.UseOkta && !environment.UseMsal) {
        this.checkLicense(observer);
      } else if (environment.UseMsal) {
        const msalInstance = msalConfig.MSALInstanceFactory();
        // Wait for MSAL redirect to finish, so the token is attachedd
        msalInstance
          .handleRedirectPromise()
          .then((_tokenResponse) => {
            this.checkLicense(observer);
          })
          .catch((_error) => {
            // Handle redirect error
            this.handleError(observer, 'Failed to load licensing information from the server!');
          });
      } else if (environment.UseOkta) {
        const oktaAuth: OktaAuth = this.injector.get(OKTA_AUTH);

        const idToken = oktaAuth.getIdToken();//JLL uses Okta id_token

        if (idToken) {
          this.checkLicense(observer);
        }
      }
    });
  }

  /**
   * Checks if the user is licensed. For L3, an active user is always licensed
   * @param observer Observer that should return the result
   */
  private checkLicense(observer): void {
    this.http.get(`${environment.POLLUX_API_URL}/user/current`)
      .pipe(take(1))
      .subscribe({
        next: (data: any) => {
          if (data?.OID) {
            console.log('License is valid');
            observer.next(true);
          }
          else {
            this.handleError(observer, 'License is invalid');
          }

          observer.complete();
        },
        error: (_error: any) => {
          this.handleError(observer, 'Failed to load licensing information from the server!');
        }
      });
  }

  /**
   * Error handler
   * @param observer Observer that should return the result
   * @param message Error message
   */
  private handleError(observer, message): void {
    console.log(message || '');
    this.router.navigate(['unauthorized']);
    observer.next(false);
    observer.complete();
  }
}
